/**
 * Sign rules: a number column whose sign follows another column of the same
 * row — Quantity positive when Transaction is Buy, negative when it is Sell.
 *
 * It changes what is stored, not how it looks: the status bar and every sum
 * read the stored value, so a "show as negative" format would display -584 and
 * still add +584. The grid applies it as the edit is made (`Grid.tsx`,
 * `applyCellChanges`) and the database enforces the same rule on every write
 * (`private.apply_sign_rules`, migration sign_rules) so imports, the API and
 * MCP follow it too. Keep the two in step.
 *
 * The sign is applied to the absolute value, so typing 5 or -5 lands the same,
 * and a row whose condition matches neither list keeps what was typed.
 */
export interface GridSignRule {
  /** The column whose value decides the sign. */
  readonly field: string;
  /** Values of `field` that make this column positive. */
  readonly positive: readonly string[];
  /** Values of `field` that make this column negative. */
  readonly negative: readonly string[];
}

const normalizeValues = (value: unknown): string[] =>
  Array.isArray(value)
    ? [...new Set(value.filter((v): v is string => typeof v === 'string').map((v) => v.trim()).filter(Boolean))]
    : [];

export function normalizeSignRule(value: unknown): GridSignRule | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const raw = value as { field?: unknown; positive?: unknown; negative?: unknown };
  if (typeof raw.field !== 'string' || raw.field === '') return null;
  const positive = normalizeValues(raw.positive);
  const negative = normalizeValues(raw.negative);
  if (positive.length === 0 && negative.length === 0) return null;
  return { field: raw.field, positive, negative };
}

const matches = (list: readonly string[], value: string) =>
  list.some((entry) => entry.toLowerCase() === value);

/** The sign the rule asks for given the condition cell, or 0 for "leave as typed". */
export function signFor(rule: GridSignRule, condition: unknown): 1 | -1 | 0 {
  if (condition === null || condition === undefined) return 0;
  const value = String(condition).trim().toLowerCase();
  if (value === '') return 0;
  if (matches(rule.negative, value)) return -1;
  if (matches(rule.positive, value)) return 1;
  return 0;
}

/**
 * `value` with the sign the rule asks for, keeping its storage type (a number
 * stays a number, a numeric string a string). `undefined` means unchanged:
 * not a number, zero, no matching condition, or already signed right.
 */
export function signedValue(rule: GridSignRule, value: unknown, condition: unknown): unknown {
  const sign = signFor(rule, condition);
  if (sign === 0) return undefined;
  if (typeof value === 'number') {
    if (!Number.isFinite(value) || value === 0 || Math.sign(value) === sign) return undefined;
    return Math.abs(value) * sign;
  }
  // A numeric string keeps its exact digits ("5.50" → "-5.50"); only the minus
  // moves. The database does the same, so both sides store the same text.
  if (typeof value !== 'string') return undefined;
  const text = value.trim();
  if (!NUMERIC.test(text) || Number(text) === 0) return undefined;
  const negative = text.startsWith('-');
  if (negative === (sign < 0)) return undefined;
  return negative ? text.slice(1) : `-${text}`;
}

/** Plain decimal text, the same pattern the database accepts. */
const NUMERIC = /^-?[0-9]+(\.[0-9]+)?([eE][-+]?[0-9]+)?$/;

/**
 * The cells of `row` the rules would change, as field → new value. Only rules
 * whose own column or condition column is in `touched` are looked at, unless
 * `touched` is omitted (a rewrite of existing rows).
 */
export function signChanges(
  rules: Readonly<Record<string, GridSignRule>>,
  row: Readonly<Record<string, unknown>>,
  touched?: ReadonlySet<string>,
): Map<string, unknown> {
  const out = new Map<string, unknown>();
  for (const [field, rule] of Object.entries(rules)) {
    if (touched && !touched.has(field) && !touched.has(rule.field)) continue;
    const next = signedValue(rule, row[field], row[rule.field]);
    if (next !== undefined) out.set(field, next);
  }
  return out;
}
